/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { useState } from "react";
import { FieldPath, UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { TableCell } from "@/components/ui/table";

import { misc } from "@/lib/misc";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { cn } from "@/lib/utils";

export const TableCellCurrencyInput = <T extends z.ZodTypeAny>({
  disabled,
  fieldName,
  form,
  handleOnBlur,
  index,
  listName,
  rowId,
  showError,
}: {
  disabled?: boolean;
  fieldName: string;
  form: UseFormReturn<z.infer<T>>;
  handleOnBlur: (
    id: number,
    fieldName: string,
    updatedValue: string | null,
    type?: "date" | "number",
  ) => void;
  index: number;
  listName: "placements" | "requisitions";
  rowId: number;
  showError?: (
    form: UseFormReturn<z.infer<T>>,
    fieldName: FieldPath<z.infer<T>> | string,
  ) => string;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const fieldNamePath = `${listName}.${index}.${fieldName}` as FieldPath<
    z.infer<T>
  >;

  return (
    <TableCell className={cn("p-1", showError?.(form, fieldNamePath))}>
      <FormField
        control={form.control}
        name={fieldNamePath}
        render={({ field }) => {
          const displayValue =
            isEditing || field.value === "" || field.value === null
              ? ((field.value || "") as number)
              : // Enforce that field.value is treated as a number for the toLocaleString() call. Zod sends it in as a string.
                `$${Number(field.value).toLocaleString() || ""}`;
          return (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  id={`cell-${fieldName}-${index}`}
                  className={cn(
                    "rounded-sm border-transparent bg-transparent",
                    !disabled &&
                      "hover:border-slate-300 focus:border-slate-300",
                  )}
                  disabled={disabled}
                  placeholder="-"
                  value={displayValue}
                  onBlur={() => {
                    setIsEditing(false);
                    void form
                      .trigger(fieldNamePath, { shouldFocus: false })
                      .then((ok) => {
                        if (!ok) return;
                        handleOnBlur(
                          rowId,
                          fieldName,
                          field.value as string,
                          "number",
                        );
                        mixpanelUtils.trackInlineEdit(
                          fieldName,
                          getScreenFromPathname(location.pathname),
                        );
                      });
                  }}
                  onChange={(e) => {
                    const newFieldValue = !e.target.value
                      ? null
                      : e.target.value;
                    field.onChange(newFieldValue);
                  }}
                  onKeyDown={misc.enforceCurrencyInput}
                  onFocus={() => {
                    setIsEditing(true);
                  }}
                />
              </FormControl>
            </FormItem>
          );
        }}
      />
    </TableCell>
  );
};
